import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { usePromotions } from "../../context/promotionsContext/promotionsContext";

export default function FlashSaleBanner() {
  const { activePromotions } = usePromotions();

  const salePromotion = useMemo(() => {
    return activePromotions.find(
      (promotion) =>
        promotion?.promoType === "percentage_off" &&
        Number(promotion?.discountPercent) === 40 &&
        promotion?.scopeType === "global"
    );
  }, [activePromotions]);

  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!salePromotion?.endDate) return;

    const updateTimer = () => {
      const now = new Date().getTime();
      const end = new Date(salePromotion.endDate).getTime();
      const distance = end - now;

      if (distance <= 0) {
        setTimeLeft(null);
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((distance / (1000 * 60)) % 60),
        seconds: Math.floor((distance / 1000) % 60),
      });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [salePromotion]);

  if (!salePromotion) return null;

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "18px",
        overflow: "hidden",
        border: "1px solid rgba(255, 180, 70, 0.65)",
        background: `
          radial-gradient(circle at 10% 20%, rgba(255, 204, 77, 0.38), transparent 34%),
          radial-gradient(circle at 90% 80%, rgba(255, 138, 0, 0.34), transparent 38%),
          linear-gradient(135deg, #22160a 0%, #111216 55%, #1c1208 100%)
        `,
        boxShadow: "0 10px 28px rgba(0,0,0,0.28)",
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, sm: 2.25, md: 3 },
          py: { xs: 1.35, md: 1.65 },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 1.25, md: 2 }}
          alignItems="center"
          justifyContent="space-between"
          textAlign={{ xs: "center", md: "left" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: "#ffcc4d",
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontSize: { xs: "0.78rem", md: "0.88rem" },
                lineHeight: 1.1,
              }}
            >
              Flash Sale
            </Typography>

            <Typography
              sx={{
                color: "#f7f7f7",
                fontWeight: 900,
                lineHeight: 1.05,
                fontSize: { xs: "1.75rem", sm: "2.1rem", md: "2.45rem" },
                letterSpacing: "-0.035em",
              }}
            >
              40% Off All Plugins
            </Typography>
          </Box>

          {timeLeft && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={0.8}
              alignItems="center"
              justifyContent="center"
              sx={{ flexShrink: 0 }}
            >
              <Typography
                sx={{
                  color: "#ffcc4d",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  fontSize: { xs: "0.72rem", md: "0.82rem" },
                  whiteSpace: "nowrap",
                }}
              >
                Offer Ends In:
              </Typography>

              <Stack direction="row" spacing={0.75} justifyContent="center">
                <CountdownBox label="days" value={timeLeft.days} />
                <CountdownBox label="hrs" value={timeLeft.hours} />
                <CountdownBox label="min" value={timeLeft.minutes} />
                <CountdownBox label="sec" value={timeLeft.seconds} />
              </Stack>
            </Stack>
          )}

          <Button
            component={RouterLink}
            to="/plugins"
            sx={{
              borderRadius: "6px",
              px: 2.4,
              py: 0.8,
              textTransform: "none",
              fontWeight: 900,
              color: "#111",
              backgroundColor: "#ffcc4d",
              border: "1px solid rgba(255,255,255,0.16)",
              minWidth: { xs: "100%", sm: 150 },
              "&:hover": {
                backgroundColor: "#ffd766",
                transform: "translateY(-1px)",
              },
            }}
          >
            Shop the Sale
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

function CountdownBox({ value, label }) {
  const displayValue = String(value).padStart(2, "0");

  return (
    <Box
      sx={{
        minWidth: { xs: 54, sm: 60 },
        px: 0.8,
        py: 0.65,
        borderRadius: "10px",
        background: "linear-gradient(180deg, #fff7df 0%, #ffd166 100%)",
        border: "1px solid rgba(255,255,255,0.35)",
        boxShadow: "0 5px 14px rgba(0,0,0,0.22)",
        textAlign: "center",
      }}
    >
      <Typography
        sx={{
          color: "#111",
          fontWeight: 900,
          fontSize: { xs: "1rem", sm: "1.15rem" },
          lineHeight: 1,
        }}
      >
        {displayValue}
      </Typography>

      <Typography
        sx={{
          color: "rgba(0,0,0,0.68)",
          fontWeight: 800,
          fontSize: "0.68rem",
          lineHeight: 1.15,
          mt: 0.35,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}